import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/* KyDeco cart drawer add-ons. Same add + re-render pattern as free-rug-gift.js:
   POST /cart/add with the drawer's section ids, then dispatch CartAddEvent so the
   theme swaps in the fresh drawer markup (which drops the just-added card). */
const SOURCE = 'kd-cart-addons';

class KdCartAddons extends HTMLElement {
  #busy = false;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-kd-addon-add]') : null;
    if (!(button instanceof HTMLButtonElement)) return;
    const variantId = button.dataset.variantId;
    if (!variantId) return;
    this.#add(variantId, button);
  };

  async #add(variantId, button) {
    if (this.#busy) return;
    this.#busy = true;
    this.#setBusy(true, button);
    this.#error('');

    try {
      const body = JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: this.#sectionIds.join(','),
        sections_url: window.location.pathname,
      });
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
      const parsed = await response.json();
      if (!response.ok || parsed.status || parsed.errors) {
        throw new Error(parsed.description || parsed.message || 'Unable to add this item.');
      }
      const cartRes = await fetch(`${Theme.routes.cart_url}.js`);
      const cart = await cartRes.json();
      this.dispatchEvent(
        new CartAddEvent(cart, SOURCE, { source: SOURCE, itemCount: cart.item_count, sections: parsed.sections })
      );
    } catch (err) {
      this.#error(err instanceof Error ? err.message : 'Unable to add this item.');
    } finally {
      this.#busy = false;
      this.#setBusy(false, button);
    }
  }

  #setBusy(isBusy, button) {
    this.toggleAttribute('aria-busy', isBusy);
    for (const b of this.querySelectorAll('[data-kd-addon-add]')) {
      if (b instanceof HTMLButtonElement) b.disabled = isBusy;
    }
    const label = button && button.querySelector('[data-kd-addon-text]');
    if (label) label.textContent = isBusy ? 'Adding' : 'Add';
  }

  #error(msg) {
    const el = this.querySelector('[data-kd-addon-error]');
    if (!(el instanceof HTMLElement)) return;
    el.textContent = msg;
    el.hidden = !msg;
  }

  get #sectionIds() {
    const ids = new Set();
    if (this.dataset.sectionId) ids.add(this.dataset.sectionId);
    for (const c of document.querySelectorAll('cart-items-component[data-section-id]')) {
      if (c instanceof HTMLElement && c.dataset.sectionId) ids.add(c.dataset.sectionId);
    }
    return Array.from(ids);
  }
}

if (!customElements.get('kd-cart-addons')) {
  customElements.define('kd-cart-addons', KdCartAddons);
}
